// components/payments/PayoutsTable.tsx
import React from "react";
import {
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Building2,
  CreditCard,
} from "lucide-react";
import EmptyState from "./EmptyState";
import { Payout } from "../../lib/types/payments";

interface PayoutsTableProps {
  payouts: Payout[];
  searchQuery: string;
  statusFilter: string;
}

const PayoutsTable: React.FC<PayoutsTableProps> = ({
  payouts,
  searchQuery,
  statusFilter,
}) => {
  // Filter payouts based on search and status
  const filteredPayouts = payouts.filter((payout) => {
    const matchesSearch =
      searchQuery === "" ||
      payout.payoutId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (payout.bankName &&
        payout.bankName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (payout.paypalEmail &&
        payout.paypalEmail.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" || payout.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format amount helper
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Status badge component
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
            <CheckCircle className="w-3.5 h-3.5" />
            Completed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-100">
            <Clock className="w-3.5 h-3.5" />
            Pending
          </span>
        );
      case "processing":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Processing
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
            <XCircle className="w-3.5 h-3.5" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100">
            {status}
          </span>
        );
    }
  };

  // Get payment method display
  const getPaymentMethod = (payout: Payout) => {
    if (payout.method === "bank") {
      return (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {payout.bankName || "Bank Account"}
            </p>
            <p className="text-xs text-gray-500">
              ****{payout.accountLastFour || "****"}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-indigo-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">PayPal</p>
          <p className="text-xs text-gray-500">
            {payout.paypalEmail || "PayPal Account"}
          </p>
        </div>
      </div>
    );
  };
  const hasFilters = searchQuery !== "" || statusFilter !== "all";

  if (filteredPayouts.length === 0) {
    return <EmptyState type="transactions" hasFilters={hasFilters} />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Payout ID
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Method
            </th>
            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Gross
            </th>
            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Fees
            </th>
            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Net Amount
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {filteredPayouts.map((payout) => (
            <tr
              key={payout._id}
              className="hover:bg-gray-50/50 transition-colors"
            >
              <td className="py-4 px-4">
                <span className="text-sm font-mono text-gray-600">
                  {payout.payoutId}
                </span>
              </td>
              <td className="py-4 px-4">
                <span className="text-sm text-gray-600">
                  {formatDate(payout.createdAt)}
                </span>
              </td>
              <td className="py-4 px-4">{getPaymentMethod(payout)}</td>
              <td className="py-4 px-4 text-right">
                <span className="text-sm text-gray-900">
                  {formatAmount(payout.grossAmount)}
                </span>
              </td>
              <td className="py-4 px-4 text-right">
                <span className="text-sm text-red-500">
                  -{formatAmount(payout.fees)}
                </span>
              </td>
              <td className="py-4 px-4 text-right">
                <span className="text-sm font-semibold text-green-600">
                  {formatAmount(payout.netAmount)}
                </span>
              </td>
              <td className="py-4 px-4">{getStatusBadge(payout.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PayoutsTable;
